import { useEffect, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { BRANDS, BRAND_INFO, CHANNELS, CONTENT_TYPES } from '@hub/shared'
import type { BrandId, Channel, ContentType } from '@hub/shared'
import { ApiError } from '../lib/api.js'
import { addDays, today } from '../lib/dates.js'
import { useFilters } from '../lib/filters.js'
import { useConvertInspiration } from '../lib/inspiration.js'
import type { Inspiration } from '../lib/inspiration.js'
import { useCreateItem } from '../lib/items.js'
import { brandSelected, brandText, brandTint, channelLabel, typeLabel } from './Brand.js'
import { Icon } from './Icon.js'
import { Sheet } from './Sheet.js'

/** Which channels a type usually goes out on — pre-selected, freely changeable. */
const DEFAULT_CHANNELS: Partial<Record<ContentType, Channel[]>> = {
  newsletter: ['email'],
  social_post: ['instagram', 'facebook'],
  reel: ['instagram'],
  story: ['instagram'],
  anzeige: ['instagram', 'facebook'],
  website: ['website'],
  print: ['print'],
  messe: ['messe'],
}

/** New entry in a few taps: title, brand, type, channels, date. */
export function NewItemSheet({
  onClose,
  onCreated,
  date,
  ideaId,
}: {
  onClose: () => void
  onCreated: (id: string) => void
  date?: string | undefined
  ideaId?: string | undefined
}): JSX.Element {
  const { filters } = useFilters()
  const client = useQueryClient()
  // From the inspiration feed: the find is already in the cache of the screen it came from.
  const idea = useMemo(() => {
    if (!ideaId) return undefined
    for (const [, list] of client.getQueriesData<Inspiration[]>({ queryKey: ['inspiration'] })) {
      const hit = list?.find((x) => x.id === ideaId)
      if (hit) return hit
    }
    return undefined
  }, [client, ideaId])
  const [title, setTitle] = useState(idea?.title ?? '')
  // A single brand in the filter is almost certainly the brand of the new entry too.
  const [brand, setBrand] = useState<BrandId>(idea?.brand ?? (filters.brand.length === 1 ? filters.brand[0]! : 'pebro'))
  const [type, setType] = useState<ContentType>('social_post')
  const [channels, setChannels] = useState<Channel[]>(DEFAULT_CHANNELS.social_post ?? [])
  const [start, setStart] = useState(date ?? addDays(today(), 7))
  const [note, setNote] = useState('')
  const create = useCreateItem()
  const convert = useConvertInspiration()
  const saving = ideaId ? convert : create

  useEffect(() => {
    setChannels(DEFAULT_CHANNELS[type] ?? [])
  }, [type])


  const submit = (e: React.FormEvent): void => {
    e.preventDefault()
    const item = { brand, title, type, channels, start, note, status: 'geplant' as const }
    if (ideaId) convert.mutate({ id: ideaId, item }, { onSuccess: (created) => onCreated(created.id) })
    else create.mutate(item, { onSuccess: (created) => onCreated(created.id) })
  }

  return (
    <Sheet onClose={onClose} side={false} label="Neuer Eintrag">
      <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
        <div className="flex items-center justify-between px-5 pb-2 pt-3" data-sheet-drag>
          <div>
            <h2 className="text-h3">Neuer Eintrag</h2>
            {idea && <p className="text-caption text-label2/60">aus Inspiration: {idea.title}</p>}
          </div>
          <button type="button" onClick={onClose} className="glass-circle h-9 w-9" aria-label="Schließen">
            <Icon name="close" size={16} />
          </button>
        </div>

        <div className="min-h-0 min-w-0 flex-1 space-y-5 overflow-y-auto overflow-x-hidden overscroll-contain px-5 pb-5">
          <input
            autoFocus
            className="field text-h4 font-semibold"
            placeholder="Worum geht's?"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />

          <div className="grid grid-cols-3 gap-2">
            {BRANDS.map((b) => {
              const on = brand === b
              return (
                <button
                  key={b}
                  type="button"
                  onClick={() => setBrand(b)}
                  aria-pressed={on}
                  className="flex h-11 items-center justify-center gap-1.5 rounded-[14px] text-callout font-semibold transition-all"
                  style={on ? brandSelected(b) : { backgroundColor: brandTint(b, 8), color: brandText(b) }}
                >
                  {on && <Icon name="check" size={15} />}
                  {BRAND_INFO[b].name}
                </button>
              )
            })}
          </div>

          <div>
            <p className="section-title px-0">Art</p>
            <div className="flex flex-wrap gap-1.5">
              {CONTENT_TYPES.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setType(t)}
                  className={`h-8 rounded-full px-3 text-small transition-colors ${
                    type === t ? 'bg-label font-medium text-cell' : 'bg-fill/[0.12] text-label'
                  }`}
                >
                  {typeLabel(t)}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="section-title px-0">Kanäle</p>
            <div className="flex flex-wrap gap-1.5">
              {CHANNELS.map((c) => {
                const on = channels.includes(c)
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setChannels(on ? channels.filter((x) => x !== c) : [...channels, c])}
                    className={`h-8 rounded-full px-3 text-small transition-colors ${
                      on ? 'bg-label font-medium text-cell' : 'bg-fill/[0.12] text-label'
                    }`}
                  >
                    {channelLabel(c)}
                  </button>
                )
              })}
            </div>
          </div>

          <label className="block">
            <span className="section-title block px-0">Ausspieltag</span>
            <input type="date" className="field" value={start} onChange={(e) => setStart(e.target.value)} required />
            <span className="section-note block px-0">Der Tag, an dem es rausgeht — nicht das beworbene Ereignis.</span>
          </label>

          <textarea
            className="field min-h-[80px] py-3"
            placeholder="Notiz (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>

        <div className="hairline-top px-5 py-4">
          {saving.error && (
            <p className="mb-2 text-center text-small text-danger">
              {saving.error instanceof ApiError ? saving.error.message : 'Speichern fehlgeschlagen.'}
            </p>
          )}
          <button type="submit" className="btn-primary w-full" disabled={saving.isPending || title.trim() === ''}>
            {saving.isPending ? 'Lege an …' : idea ? 'In den Plan übernehmen' : 'Anlegen'}
          </button>
        </div>
      </form>
    </Sheet>
  )
}
